import { queryOptions, useQuery, type UseQueryResult } from '@tanstack/react-query'

import { livePolling } from '@/app/query-client'
import { homeKeys } from '@/features/home/api/keys'
import { homeService } from '@/features/home/api/homeService'
import type { HomeMoverRow, HomeMoversSide } from '@/features/home/api/homeSchemas'
import { portfolioKeys } from '@/features/portfolio/api/keys'
import { portfolioService } from '@/features/portfolio/api/portfolioService'
import { useMarketStatus } from '@/hooks/useMarketStatus'
import type { PortfolioSummary } from '@/schemas/portfolio'

/** One side of the Gainers and losers block — what Home's loader starts for both sides. Key and
 *  fetch only; the hook below adds the rest. */
export const homeMoversQueryOptions = (side: HomeMoversSide) =>
  queryOptions<HomeMoverRow[]>({
    queryKey: homeKeys.movers(side),
    queryFn: ({ client }) => homeService.movers(side, client),
  })

/** One side's five rows. Gated on market status like every other movers read — nothing in the
 *  table can move while NEPSE is shut. */
export const useHomeMovers = (side: HomeMoversSide): UseQueryResult<HomeMoverRow[]> => {
  const { isOpen } = useMarketStatus()

  return useQuery<HomeMoverRow[]>({
    ...homeMoversQueryOptions(side),
    refetchInterval: livePolling(isOpen),
  })
}

/**
 * The member's portfolio summary, on exactly the key and fetch
 * `usePortfolioSummary()` uses — so what Home's loader awaits is the cache
 * entry that hook, `HomeScreen`'s portfolio gate and `PortfolioCard` all
 * read. It caches the raw summary: the hook's `select` still turns an empty
 * book into `null` on the way out.
 *
 * TODO(tech-debt): this belongs in `features/portfolio/api/queries.ts`, with
 * `usePortfolioSummary` spreading it. It is here only because Portfolio's own
 * route conversion is deferred and its files are frozen until then; move it
 * across when that family converts, so the key and the fetch are written once.
 */
export const homePortfolioSummaryQueryOptions = () =>
  queryOptions<PortfolioSummary>({
    queryKey: portfolioKeys.summary(),
    queryFn: () => portfolioService.summary(),
  })
